/* eslint-disable import/no-cycle */
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { throttle } from 'throttle-debounce';
import { PreventUserSelect } from '../../styles/PreventUserSelect';
import { TabStyles } from '../../styles/TabStyles';
import { DisableAutoFocus } from '../../utils/AutoFocus';
import { conditional, makeTestId } from '../../utils/Declarative';
import { computePositionToContainBounds, Position } from '../../utils/Geometry';
import { GlobalPointerEventCapturer } from '../../utils/GlobalPointerEventCapturer';
import { ElemAttrs } from '../../utils/HtmlAttrs';
import { clamp } from '../../utils/Math';
import { OrientationChange } from '../../utils/OrientationChange';
import { HorizontalOriginOption, Overlay, OverlayProps, VerticalOriginOption } from '../../utils/Overlay';
import { FocusLockContext } from '../../utils/StackingComponent';
import { ViewportConstrainedDomWrapper } from '../../utils/ViewportConstrainedDom';
import * as ViewportHelpers from '../../utils/ViewportHelpers';
import { FloaterWidget, FloaterWidgetDefault } from '../FloaterWidget';
import { GlassWidget } from '../GlassWidget';
import { DialogContent } from './DialogContent';

export enum DialogPositioningBehavior {
  /** Centers the dialog on the page */
  Centered,
  /** Maximizes the dialog */
  Maximized,
  /** Makes an initally centered dialog that can be dragged (Will not work with DialogType.Uber) */
  Draggable,
  /** Positions the dialog relative to an anchor element */
  Anchored,
}

export enum DialogType {
  /** Dialog with a title bar. It can be draggable. */
  NormalWithTitleBar,
  /** Dialog without a title bar, but has a header space that makes it draggable */
  NormalWithoutTitleBar,
  /** Dialog with small top padding. It is not draggable. */
  Uber,
  /** Dialog with a large title text but no visible bar. It can be draggable. */
  LightweightTitleBar,
}

/**
 * Different modes for user-based dialog resizing (aka via drag handle).
 */
export enum DialogUserResizeMode {
  /** The dialog cannot be resized */
  NotResizable = 'not-resizable',
  /** The dialog can be resized horizontally */
  Horizontal = 'horizontal',
  /** The dialog can be resized vertically */
  Vertical = 'vertical',
  /** The dialog can be resized both vertically and horizontally */
  Both = 'both',
}

type AfterHideCallback = (redisplayCallback: () => void) => void;

export const SharedWidgetsDialogLabel: string = 'SharedWidgetsDialogLabel';
export const DialogBorderWidth = TabStyles.Sizing.BorderWidth;

const ResizeAndRepositionDelayMs = 500;
const ResizeThrottleTime = 100;
const ViewportHorizontalMargins = 40;
const ViewportVerticalMargins = 40;
let dialogInstanceCount: number = 0;

export const getStyles: () => {
  Dialog: React.CSSProperties,
} = () => {
  return {
    Dialog: {
      ['--title-bar-background-color' as string]: '#F3F3F3', // component/background/header/on-light/neutral
      backgroundColor: TabStyles.Colors.White,
      borderColor: TabStyles.Colors.L5,
      borderRadius: TabStyles.Sizing.BorderRadius,
      borderStyle: 'solid',
      borderWidth: DialogBorderWidth,
      boxShadow: TabStyles.Shadow.Elevated,
      cursor: 'default',
      display: 'flex',
      flexDirection: 'column',
    }
  };
};

/** https://x.com/mattpocockuk/status/1821926395380986219 */
type OtherWidth = (number & {});
export interface DialogProps extends DisableAutoFocus, Pick<
  OverlayProps,
  'anchorEl' | 'anchorOrigin' | 'getAnchorClientRect' | 'portalEventTrapOverrides' | 'stackedOrigin' | 'stackingRootId' | 'zIndex'
  > {
  /**
   * Test id to be attached to the dialog and its contents.
   * The suffix '-Dialog' (and various subcomponent suffixes) will be automatically added
   */
  testId: string;
  type: DialogType;
  /** Text shown in the dialog title bar, when present, and also used as the accessible label for the dialog. Always required, even when not visible. */
  title: string;
  /**
   * Whether or not to show the close button. No effect on dialog types which don't have a title bar.
   * Note: Dialogs of type `LightweightTitleBar` should never have a close button {@link http://design/guidelines/patterns/dialogs-1 Design Guidance}.
   */
  showCloseButton: boolean;
  /**
   * Callback to be fired when a close action occurs, for example pressing `esc` or clicking the close button.
   * This does NOT fire when clicking on the glass. That action is handled with the handleGlassClick since
   * there are cases where clicking on the glass should not close the dialog.
   */
  onCloseRequest: () => void;
  /** The positioning behavior to use for the dialog.  Uber dialogs can not be draggable */
  positioningBehavior: DialogPositioningBehavior;
  /**
   * If the dialog position is centered or draggable that hasn't been dragged yet, should the dialog recenter when the
   * dialog props or content changes.
   */
  shouldRecenterOnUpdate?: boolean;
  /**
   * Whether or not the dialog should be modal.  If modal, a full window div, also known as a Glass, is added underneath
   * the dialog to prevent interaction with the rest of the site, and focus is trapped within the modal dialog.
   */
  isModal: boolean;
  /** Is glass transparent or non-transparent.  Glass will only be rendered if isModal is set to true. */
  isGlassTransparent?: boolean;
  /** Handle when the Glass is clicked.  Glass will only be rendered if isModal is set to true. */
  handleGlassClick?: () => void;
  /** Custom style object to apply to the dialog element. To set CSS `width` or `height`, use the `dialogWidth`/`dialogHeight` props. */
  dialogStyle?: React.CSSProperties;
  /** Custom style object to apply to the DialogContent component. */
  dialogContentStyle?: React.CSSProperties;
  /** Custom style object to apply to the dialog's body */
  dialogBodyStyle?: React.CSSProperties;
  /** The height of the dialog content (does not include the dialog's borders). If the dialog is resizable, the initial height of the dialog. */
  dialogHeight?: number;
  /**
   * The width of the dialog content (does not include the dialog's borders). If the dialog is resizable, the initial width of the dialog.
   *
   * Standard dialog widths in the Tableau Design System are:
   * * 200
   * * 260
   * * 375
   * * 500
   * * 600
   * * 700
   *
   * Please post in #tableau-visual-design-general before using a different width.
   */
  dialogWidth?: 200 | 260 | 375 | 500 | 600 | 700 | OtherWidth;
  /**
   * The lower bound of the dialog content height (does not include the dialog's borders) that the user can drag-to-resize the dialog to.
   * @default 100
   */
  dialogMinHeight?: number;
  /**
   * The lower bound of the dialog content width (does not include the dialog's borders) that the user can drag-to-resize the dialog to.
   * @default 100
   */
  dialogMinWidth?: number;
  /** @deprecated no effect, the footer will be shown iff `footerContent` is provided. */
  showFooter?: boolean;
  /**
   * Content that will be shown in the Dialog footer. This is useful for tall dialogs because the `children` of the dialog
   * will scroll when overflown, while the Footer will always be visible at the bottom of the dialog.
   *
   * Many cases may enjoy using `DialogFooterButtonsWidget` here.
   */
  footerContent?: React.ReactNode;
  /** The content of the dialog */
  children?: React.ReactNode;
  /**
   * When the dialog closes should the focus return to the last focused element.  If the user is in a touch or mobile environment
   * returning focus may open a keyboard or do something touch specific.  As a client it must be decided what the correct behavior is.
   */
  shouldReturnFocusOnClose?: boolean;
  /**
   * This should only be true if the browser is non-mobile.  Otherwise there can be issue with with the android keyboard forcing a resize
   * and the dialog to 'shake'.
   *
   * For centered/maximized dialogs on resize it will try to remain centered
   * For dragged/anchored dialogs on resize it will try to remain on the screen
   */
  shouldAdjustPositionOnWindowResize?: boolean;
  /**
   * The dialog resize mode for user interactions.
   * @default DialogUserResizeMode.NotResizable
   */
  userResizeMode?: DialogUserResizeMode;
  /** Initial position of the dialog relative to the viewport; ignored after mounting. Only applies to `Draggable` dialogs. */
  initialPosition?: { top: number, left: number };
  /** Invoked when the user completes a dialog resize operation. */
  onUserResizeEnd?: (width: number, height: number) => void;
}

export interface DialogState {
  hasBeenDragged: boolean;
  height?: number;
  isResizing: boolean;
  width?: number;
}

export interface DialogContentProps extends DialogProps {
  id: string;
  getDraggableDialogClientRect: () => ClientRect;
  setDraggableDialogPosition: (pos: Position) => void;
}

type CardinalDirection = 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw';

export class DialogWidget extends React.Component<DialogProps, DialogState> {
  private dialogResizingEventCapturer: GlobalPointerEventCapturer | null = null;

  private throttledHandleAdjustPositionOnResize: () => void = throttle(ResizeThrottleTime, () => this.handleAdjustPositionOnResize());
  private instanceId: number;
  private floaterWidget: FloaterWidgetDefault;
  private overlay: Overlay;
  private sizingWrapper: ViewportConstrainedDomWrapper;
  private resizeAndRepositionTimeoutId: number | null;
  private resizeAndRepositionCallback: (() => void) | null;
  private dragPositionBeforeHide?: Position;

  public static defaultProps: Required<Pick<DialogProps, 'dialogMinHeight' | 'dialogMinWidth' | 'userResizeMode'>> = {
    dialogMinHeight: 100,
    dialogMinWidth: 100,
    userResizeMode: DialogUserResizeMode.NotResizable,
  };

  public constructor(props: DialogProps) {
    super(props);
    this.instanceId = dialogInstanceCount++;
    this.state = { hasBeenDragged: false, height: props.dialogHeight, isResizing: false, width: props.dialogWidth };
  }

  private addResizeEventListener(): void {
    window.addEventListener('resize', this.throttledHandleAdjustPositionOnResize);
  }

  private removeResizeEventListener(): void {
    window.removeEventListener('resize', this.throttledHandleAdjustPositionOnResize);
  }

  /**
   * On a resize event and if shouldAdjustPositionOnResizeAndIsNonMobile is true, this will fired
   * and try to fit the dialog on the screen depending upon the positioning behavior and state of
   * the dialog.
   */
  private handleAdjustPositionOnResize = (): void => {
    if (!this.props.shouldAdjustPositionOnWindowResize) { return; }

    /**
     * Defect 646231:Dialog (Beta) Issue: Inconsistent resizing
     * On window resizes we were not resizing the sizingWrapper element. As a result, the content
     * within the Dialog Widget would resize; however the sizingWrapper element would not.
     * The end user effect was inconsistent resizing of the Dialog Widget and visual artifacts.
     */
    if (this.sizingWrapper) {
      this.sizingWrapper.updateSize();
    }
    this.repositionDialog();
  }

  private resizeDialog(e: MouseEvent, rect: ClientRect, cardinal: CardinalDirection): void {
    e.preventDefault();
    const { dialogMinHeight, dialogMinWidth } = this.props;
    const position = { top: rect.top, left: rect.left };
    const dimensions = { height: rect.height - (DialogBorderWidth * 2), width: rect.width - (DialogBorderWidth * 2) };
    const clientY = clamp(e.clientY, 0, document.documentElement.clientHeight);
    const clientX = clamp(e.clientX, 0, document.documentElement.clientWidth);

    if (cardinal.indexOf('s') >= 0) {
      dimensions.height = Math.max(clientY - rect.top, dialogMinHeight!) - (DialogBorderWidth * 2);
    } else if (cardinal.indexOf('n') >= 0) {
      dimensions.height = rect.bottom - clientY - (DialogBorderWidth * 2);
      if (dimensions.height >= dialogMinHeight!) {
        position.top = clientY;
      } else {
        dimensions.height = dialogMinHeight! - (DialogBorderWidth * 2);
        position.top = rect.bottom - dimensions.height - (DialogBorderWidth * 2);
      }
    }

    if (cardinal.indexOf('e') >= 0) {
      dimensions.width = Math.max(clientX - rect.left, dialogMinWidth!) - (DialogBorderWidth * 2);
    } else if (cardinal.indexOf('w') >= 0) {
      dimensions.width = rect.right - clientX - (DialogBorderWidth * 2);
      if (dimensions.width >= dialogMinWidth!) {
        position.left = clientX;
      } else {
        dimensions.width = dialogMinWidth! - (DialogBorderWidth * 2);
        position.left = rect.right - dimensions.width - (DialogBorderWidth * 2);
      }
    }

    if (this.floaterWidget) {
      this.floaterWidget.setPosition(position);
      this.setState(dimensions);
    }
  }

  private startResizing = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) {
      e.preventDefault();
      const startingRect = this.floaterWidget.getFloaterClientRect();
      const cardinal = e.currentTarget.getAttribute('data-cardinal') as CardinalDirection;

      this.dialogResizingEventCapturer = new GlobalPointerEventCapturer(
        mouseMoveEvent => this.resizeDialog(mouseMoveEvent as MouseEvent, startingRect, cardinal),
        this.stopResizing
      );
      this.dialogResizingEventCapturer.addListeners();
      this.setState({ isResizing: true });
    }
  }

  private stopResizing = (): void => {
    this.dialogResizingEventCapturer?.removeListeners();
    this.dialogResizingEventCapturer = null;
    this.setState({ isResizing: false });
    if (this.props.onUserResizeEnd) {
      this.props.onUserResizeEnd(this.state.width!, this.state.height!);
    }
  }

  private repositionDialog(): void {
    switch (this.props.positioningBehavior) {
      case DialogPositioningBehavior.Centered:
      case DialogPositioningBehavior.Maximized:
        this.floaterWidget.centerInVisualViewport();
        break;
      case DialogPositioningBehavior.Draggable:
        if (this.state.hasBeenDragged) {
          this.repositionDraggedDialog();
        } else {
          this.floaterWidget.centerInVisualViewport();
        }
        break;
      case DialogPositioningBehavior.Anchored:
        if (this.overlay) {
          this.overlay.setPlacement();
        }
        break;
      default:
        throw new Error(`Invalid Dialog Positioning Behavior: ${this.props.positioningBehavior}`);
    }
  }

  private handleFloaterWidgetRef = (ref: FloaterWidgetDefault): void => {
    this.floaterWidget = ref;
  }

  private handleOverlayRef = (ref: Overlay): void => {
    this.overlay = ref;
  }

  private handleSizingWrapperRef = (ref: ViewportConstrainedDomWrapper): void => {
    this.sizingWrapper = ref;
  }

  private getDialogFloaterClientRect = (): ClientRect => {
    if (this.floaterWidget) {
      return this.floaterWidget.getFloaterClientRect();
    }
    throw new Error("Can't get draggable size of non-draggable dialog");
  }

  private dragBoundedDialogFloaterPosition = (pos: Position): void => {
    if (!this.state.hasBeenDragged) {
      this.setState({ hasBeenDragged: true });
    }
    if (this.floaterWidget) {
      this.floaterWidget.setPosition(this.getBoundedFloaterPos(pos));
    }
  }

  private handleOrientationChange = (): void => {
    if (this.resizeAndRepositionTimeoutId) {
      /*
       * A new orientation change has occurred while waiting for the resize/reposition delay. We don't actually need
       * to do the preparation routine, we just need to reset the delay.
       */
      clearTimeout(this.resizeAndRepositionTimeoutId);
      this.resizeAndRepositionTimeoutId = window.setTimeout(this.resizeAndRepositionCallback!, ResizeAndRepositionDelayMs);
      return;
    }

    /**
     * After an orientation change the dialog may be in a position where it skews the visual viewport by being off the
     * page, so we need to hide it first.
     */
    this.hideDialogAndThen((redisplayCallback) => {
      /**
       * For whatever reason iOS Safari is slow to update the visual viewport after hiding the dialog after an orientation
       * change. The hack below fixes the visual jerkiness, but we still need a setTimeout for the internal values to update.
       */
      this.forceRepaintHack();

      /* Delay the resize and reposition so that we get the correct viewport values. */
      this.resizeAndRepositionCallback = () => {
        this.resizeAndRepositionTimeoutId = null;
        this.resizeAndRepositionCallback = null;

        this.sizingWrapper.updateSize();
        if (redisplayCallback) {
          redisplayCallback();
        }
        this.repositionDialog();
      };

      this.resizeAndRepositionTimeoutId = window.setTimeout(this.resizeAndRepositionCallback, ResizeAndRepositionDelayMs);
    });
  }

  /**
   * We save the dragged dialog position before hiding it because it is needed to reposition the dragged dialog.
   */
  private saveDraggedDialogPositionBeforeHide(): void {
    this.dragPositionBeforeHide = this.getDialogFloaterClientRect() as Position;
  }

  /**
   * To reposition a dragged dialog we need to take into account if it was hidden.  If it was hidden we use the
   * position before it was hidden. We need to use a single setPosition instead of one each for unhiding and repositioning
   * the dialog because if the original position before it was hidden is to the right of the viewport mobile chrome will try
   * to zoom out to show the dialog.
   */
  private repositionDraggedDialog(): void {
    let draggedOriginalPosition: Position;

    if (this.dragPositionBeforeHide) {
      draggedOriginalPosition = this.dragPositionBeforeHide;
      // set it as undefined so if a reposition occurs from an window resize change it will not set this hide variable
      this.dragPositionBeforeHide = undefined;
    } else {
      draggedOriginalPosition = this.getDialogFloaterClientRect() as Position;
    }

    this.floaterWidget.setPosition(this.getBoundedFloaterPos(draggedOriginalPosition));
  }

  /**
   * Hides the dialog and then invokes the given callback. The given callback will be invoked with a reposition callback
   * as an argument. When invoked, the reposition callback will unhide and reposition the dialog.
   */
  private hideDialogAndThen(afterHideCallback: AfterHideCallback): void {
    let redisplayCallback: () => void = () => { return; };

    switch (this.props.positioningBehavior) {
      case DialogPositioningBehavior.Centered:
      case DialogPositioningBehavior.Maximized:
        this.floaterWidget.setPosition({ top: -10000, left: -10000 });
        break;
      case DialogPositioningBehavior.Draggable:
        this.saveDraggedDialogPositionBeforeHide();
        this.floaterWidget.setPosition({ top: -10000, left: -10000 });
        break;
      case DialogPositioningBehavior.Anchored: {
        // eslint-disable-next-line react/no-find-dom-node
        const node = ReactDOM.findDOMNode(this.sizingWrapper) as HTMLElement;
        const savedDisplay = node.style.display;
        node.style.display = 'none';
        redisplayCallback = () => {
          node.style.display = savedDisplay;
        };
        break;
      }
      default:
        throw new Error(`Invalid Dialog Positioning Behavior: ${this.props.positioningBehavior}`);
    }

    afterHideCallback(redisplayCallback);
  }

  /* Forces a repaint in WebKit by querying offsetHeight. */
  private forceRepaintHack(): void {
    /**
     * The code here is adapted from
     * http://stackoverflow.com/questions/3485365/how-can-i-force-webkit-to-redraw-repaint-to-propagate-style-changes
     */
    // eslint-disable-next-line react/no-find-dom-node
    const node = ReactDOM.findDOMNode(this) as HTMLElement;
    const savedDisplay = node.style.display;
    node.style.display = 'none';
        node.offsetHeight;
    node.style.display = savedDisplay;
  }

  /**
   * Adjusts the given position if needed to keep the dialog on screen.
   */
  private getBoundedFloaterPos(pos: Position): Position {
    const dialogClientRect = this.floaterWidget.getFloaterClientRect();

    const dialogRectAtGivenPos = {
      top: pos.top,
      left: pos.left,
      width: dialogClientRect.width,
      height: dialogClientRect.height
    };

    const visualViewport = ViewportHelpers.getVisualViewport();
    const windowRect = {
      top: 0,
      left: 0,
      width: visualViewport.width,
      height: visualViewport.height
    };
    return computePositionToContainBounds(dialogRectAtGivenPos, windowRect);
  }

    private getAnchorClientRect = (): ClientRect | undefined => {
    const originalRect = this.props.getAnchorClientRect?.() || this.props.anchorEl?.getBoundingClientRect();
    if (!originalRect) {
      return undefined;
    }

    const leftOffset = (parseFloat(this.props.dialogStyle?.left as string) || 0);
    const topOffset = (parseFloat(this.props.dialogStyle?.top as string) || 0);
    return {
      left: originalRect.left + leftOffset,
      top: originalRect.top + topOffset,
      bottom: originalRect.bottom + topOffset,
      right: originalRect.right + leftOffset,
      height: originalRect.height,
      width: originalRect.width,
    };
  }

  private getPositionedDialogElem(dialogBody: JSX.Element): JSX.Element {
    switch (this.props.positioningBehavior) {
      case DialogPositioningBehavior.Centered:
      case DialogPositioningBehavior.Draggable:
      case DialogPositioningBehavior.Maximized: {
        const id: string = makeTestId(this.props.testId, 'Dialog');
        return <FloaterWidget
          portalEventTrapOverrides={this.props.portalEventTrapOverrides}
          testId={id}
          // eslint-disable-next-line deprecation/deprecation
          stackingRootId={this.props.stackingRootId}
          ref={this.handleFloaterWidgetRef}
          zIndex={this.props.zIndex}
          shouldRecenterOnUpdate={!!this.props.shouldRecenterOnUpdate && !this.state.hasBeenDragged}
          wrapperTestId={makeTestId(id, 'Floater')}
          children={dialogBody}
        />;
      }
      case DialogPositioningBehavior.Anchored: {
        const overlayProps: OverlayProps = {
          portalEventTrapOverrides: this.props.portalEventTrapOverrides,
          getAnchorClientRect: this.getAnchorClientRect,
          anchorOrigin: this.props.anchorOrigin || {
            vertical: VerticalOriginOption.bottom,
            horizontal: HorizontalOriginOption.left
          },
          stackedOrigin: this.props.stackedOrigin || {
            vertical: VerticalOriginOption.top,
            horizontal: HorizontalOriginOption.left
          },
          // eslint-disable-next-line deprecation/deprecation
          stackingRootId: this.props.stackingRootId,
          allowAutoPosition: true,
          zIndex: this.props.zIndex,
        };
        return <Overlay {...overlayProps} ref={this.handleOverlayRef} children={dialogBody} />;
      }
      default:
        throw new Error(`Invalid Dialog Positioning Behavior: ${this.props.positioningBehavior}`);
    }
  }

  private getGlassElem(): JSX.Element {
    const id: string = makeTestId(this.props.testId, 'Dialog');
    return (<GlassWidget
      testId={id}
      handleClick={this.props.handleGlassClick}
      isTransparent={this.props.isGlassTransparent}
      // eslint-disable-next-line deprecation/deprecation
      stackingRootId={this.props.stackingRootId}
      zIndex={this.props.zIndex}
      handleEscape={this.handleGlassEscape}
      wrapperTestId={makeTestId(id, 'Glass')}
    />
    );
  }

  private handleGlassEscape = (): void => {
    this.props.onCloseRequest();
  }

  public componentDidMount(): void {
    this.warnIfNoTitleProp();
    if (this.props.initialPosition && this.props.positioningBehavior === DialogPositioningBehavior.Draggable) {
      this.floaterWidget.setPosition(this.getBoundedFloaterPos(this.props.initialPosition));
    }
    OrientationChange.addListener(this.handleOrientationChange);
    if (this.props.shouldAdjustPositionOnWindowResize) {
      this.addResizeEventListener();
    }
  }

  public componentDidUpdate(prevProps: DialogProps): void {
    if (prevProps.title) {
      this.warnIfNoTitleProp();
    }
    if (this.props.shouldAdjustPositionOnWindowResize !== prevProps.shouldAdjustPositionOnWindowResize) {
      this.removeResizeEventListener();
      if (this.props.shouldAdjustPositionOnWindowResize) {
        this.addResizeEventListener();
      }
    }
    this.resizeDialogIfResizeModeChanged(prevProps.userResizeMode!, this.props.userResizeMode!);
  }

  /** It was a historical mistake that this prop was not marked as required; to nudge better practice we console warn. */
  private warnIfNoTitleProp(): void {
    if (!this.props.title) {
      console.trace('`title` prop must be provided for all dialog types, regardless of visibility, for proper accessibility');
    }
  }

  /** if dimension(s) changed from not-resizable to resizable or vice-versa, resets the dimension(s) state with the dimension prop(s) */
  private resizeDialogIfResizeModeChanged(prevResizeMode: DialogUserResizeMode, newResizeMode: DialogUserResizeMode): void {
    const wasHorizontallyResizable = prevResizeMode === 'horizontal' || prevResizeMode === 'both';
    const isHorizontallyResizable = newResizeMode === 'horizontal' || newResizeMode === 'both';
    if (wasHorizontallyResizable !== isHorizontallyResizable) {
      this.setState({ width: this.props.dialogWidth });
    }

    const wasVerticallyResizable = prevResizeMode === 'vertical' || prevResizeMode === 'both';
    const isVerticallyResizable = newResizeMode === 'vertical' || newResizeMode === 'both';
    if (wasVerticallyResizable !== isVerticallyResizable) {
      this.setState({ height: this.props.dialogHeight });
    }
  }

  public componentWillUnmount(): void {
    OrientationChange.removeListener(this.handleOrientationChange);
    this.removeResizeEventListener();
    if (this.dialogResizingEventCapturer) {
      this.dialogResizingEventCapturer.removeListeners();
    }
  }

  public render(): JSX.Element {
    const dialogEl = <ViewportConstrainedDomWrapper
      isMaximized={this.props.positioningBehavior === DialogPositioningBehavior.Maximized}
      horizontalMargins={ViewportHorizontalMargins}
      verticalMargins={ViewportVerticalMargins}
      style={{
        ...getStyles().Dialog,
        ...this.props.dialogStyle,
        width: this.state.width,
        height: this.state.height,
      }}
      ref={this.handleSizingWrapperRef}
      testId={conditional(this.props.positioningBehavior === DialogPositioningBehavior.Anchored, makeTestId(this.props.testId, 'Dialog'))}
    >
      <DialogContent
        {...this.props}
        dialogContentStyle={{
          ...this.props.dialogContentStyle,
          ...(this.state.isResizing && { pointerEvents: 'none' })
        }}
        id={`${SharedWidgetsDialogLabel}${this.instanceId}`}
        getDraggableDialogClientRect={this.getDialogFloaterClientRect}
        setDraggableDialogPosition={this.dragBoundedDialogFloaterPosition}
        testId={makeTestId(this.props.testId, 'Dialog')}
      />
      {this.props.userResizeMode !== DialogUserResizeMode.NotResizable && this.renderResizeHandles()}
    </ViewportConstrainedDomWrapper>;

    return (
      <div style={{ display: 'none' }}>
        {conditional(this.props.isModal, this.getGlassElem())}
        <FocusLockContext.Provider value={{ enableFocusLock: this.props.isModal, returnFocus: this.props.shouldReturnFocusOnClose }}>
          {this.getPositionedDialogElem(dialogEl)}
        </FocusLockContext.Provider>
      </div>
    );
  }

  private renderResizeHandles(): JSX.Element {
    const { userResizeMode } = this.props;
    return <>
      {(userResizeMode === DialogUserResizeMode.Vertical || userResizeMode === DialogUserResizeMode.Both) && <>
        <VerticalResizeHandle data-cardinal='n' onMouseDown={this.startResizing} style={{ top: -3 }} />
        <VerticalResizeHandle data-cardinal='s' onMouseDown={this.startResizing} style={{ bottom: -3 }} />
      </>}
      {(userResizeMode === DialogUserResizeMode.Horizontal || userResizeMode === DialogUserResizeMode.Both) && <>
        <HorizontalResizeHandle data-cardinal='w' onMouseDown={this.startResizing} style={{ left: -3 }} />
        <HorizontalResizeHandle data-cardinal='e' onMouseDown={this.startResizing} style={{ right: -3 }} />
      </>}
      {userResizeMode === DialogUserResizeMode.Both && <>
        <CornerResizeHandle data-cardinal='nw' onMouseDown={this.startResizing} style={{ top: -3, left: -3, cursor: 'nwse-resize' }} />
        <CornerResizeHandle data-cardinal='se' onMouseDown={this.startResizing} style={{ bottom: -3, right: -3, cursor: 'nwse-resize' }} />

        <CornerResizeHandle data-cardinal='ne' onMouseDown={this.startResizing} style={{ top: -3, right: -3, cursor: 'nesw-resize' }} />
        <CornerResizeHandle data-cardinal='sw' onMouseDown={this.startResizing} style={{ bottom: -3, left: -3, cursor: 'nesw-resize' }} />
      </>}
    </>;
  }
}

const cancelEvent: EventListener = e => e.preventDefault();
const ignoreTouchEvents = (ref: HTMLDivElement | null) => ref?.addEventListener('touchstart', cancelEvent, { passive: false });

type ResizeHandleProps = ElemAttrs & { 'data-cardinal': CardinalDirection };
const ResizeHandle: React.FC<ResizeHandleProps> = props =>
  <div {...props} ref={ignoreTouchEvents} style={{ ...PreventUserSelect, position: 'absolute', ...props.style }} />;
ResizeHandle.displayName = 'ResizeHandle';

const VerticalResizeHandle: React.FC<ResizeHandleProps> = props =>
  <ResizeHandle {...props} style={{ cursor: 'ns-resize', left: 0, right: 0, height: 8, ...props.style }} />;
VerticalResizeHandle.displayName = 'VerticalResizeHandle';

const HorizontalResizeHandle: React.FC<ResizeHandleProps> = props =>
  <ResizeHandle {...props} style={{ cursor: 'ew-resize', top: 0, bottom: 0, width: 8, ...props.style }} />;
HorizontalResizeHandle.displayName = 'HorizontalResizeHandle';

const CornerResizeHandle: React.FC<ResizeHandleProps> = props =>
  <ResizeHandle {...props} style={{ height: 8, width: 8, ...props.style }} />;
CornerResizeHandle.displayName = 'CornerResizeHandle';
